import React, { createContext, useEffect, useReducer } from 'react'
import jwtDecode from 'jwt-decode'
import axios from 'axios'
import _ from 'lodash'
import { MatxLoading } from 'app/components'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const initialState = {
  isAuthenticated: false,
  isInitialised: false,
  token: null,
  user: null,
  application: []
}

const isValidToken = (token) => {
  if (!token) return false
  try {
    const decodedToken = jwtDecode(token)
    const currentTime = Date.now() / 1000
    return decodedToken.exp > currentTime
  } catch (err) {
    return false
  }
}

const setSession = (token, user, application) => {
  if (token && user) {
    localStorage.setItem('token', token)
    localStorage.setItem('user', user)
    localStorage.setItem('apps', application)
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('apps')
    delete axios.defaults.headers.common.Authorization
  }
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      const { token, isAuthenticated, user, application } = action.payload
      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
        application,
        token
      }
    }
    case 'LOGIN': {
      const { token, user, application } = action.payload
      return {
        ...state,
        isAuthenticated: true,
        user,
        application,
        token
      }
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        application: [],
        token: null
      }
    }
    default: {
      return { ...state }
    }
  }
}

const AuthContext = createContext({
  ...initialState,
  method: 'JWT',
  glogin: () => Promise.resolve(),
  login: () => Promise.resolve(),
  logInFromEmail: () => Promise.resolve(),
  logout: () => {}
})

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const glogin = async (email, fullname, images) => {
    if (email.match(/@pyfa.co.id|@sarimurni.co.id|@holipharma.co.id|@sbd.id|@ethica.co.id|@winco.co.id|@momogi.co.id|@vivahealth.co.id|@dokterin.co.id|@farmaku.com/g)) {
      try {
        const auth = await axios.post(process.env.REACT_APP_AUTH_API + 'oauth', {
          email, fullname
        })

        if (auth.data.success) {
          const { user, token, application } = auth.data.payload

          if (application.length) {
            user.fullname = fullname
            user.images = images

            setSession(token, JSON.stringify(user), JSON.stringify(application))

            dispatch({
              type: 'LOGIN',
              payload: { user, application: application, token }
            })

            toast.success(`Hello, ${user.fullname}. Welcome to your dashboard.`, {
              containerId: "LoginApp",
              theme: 'light',
              type: "success",
              position: "top-center"
            })
          } else {
            toast.error("Login failed, please contact your administrator to request access.", {
              containerId: "LoginApp", theme: 'light', type: "error", position: "top-center"
            })
          }
        } else {
          toast.error("Login failed, please try again.", {
            containerId: "LoginApp", theme: 'light', type: "error", position: "top-center"
          })
        }
      } catch (err) {
        toast.error("Login failed, please try again.", {
          containerId: "LoginApp", theme: 'light', type: "error", position: "top-center"
        })
      }
    } else {
      toast.error("Your email is outside the organization.", {
        containerId: "LoginApp", theme: 'light', type: "error", position: "top-center"
      })
    }
  }

  const login = async (email, password) => {
    if (email.match(/@pyfa.co.id|@sarimurni.co.id|@holipharma.co.id|@sbd.id|@ethica.co.id|@winco.co.id|@momogi.co.id|@vivahealth.co.id|@dokterin.co.id|@farmaku.com/g)) {
      try {
        const auth = await axios.post(process.env.REACT_APP_AUTH_API + 'signin', {
          email, password
        })

        if (auth.data.success) {
          const { user, token, application } = auth.data.payload

          if (application.length) {
            setSession(token, JSON.stringify(user), JSON.stringify(application))

            dispatch({
              type: 'LOGIN',
              payload: { user, application: application, token }
            })

            toast.success(`Hey ${user.Name}, welcome back! Your memosign application is ready.`, {
              containerId: "LoginApp", theme: 'light', type: "success", position: "top-center"
            })
            return auth.data;
          } else {
            toast.error("Login failed, please contact your administrator.", {
              containerId: "LoginApp", theme: 'light', type: "error", position: "top-center"
            })
          }
        } else {
          toast.error("Login failed, please try again.", {
            containerId: "LoginApp", theme: 'light', type: "error", position: "top-center"
          })
          return auth.data;
        }
      } catch (err) {
        toast.error("Login failed, please try again.", {
          containerId: "LoginApp", theme: 'light', type: "error", position: "top-center"
        })
      }
    } else {
      toast.error("Your email is outside the organization.", {
        containerId: "LoginApp", theme: 'light', type: "error", position: "top-center"
      })
    }
  }

  const logout = (navigate) => {
    setSession(null, null, null);
    dispatch({ type: 'LOGOUT' });
    if (navigate) navigate('/session/signin', { replace: true });
  }

  const logInFromEmail = async (credential) => {
    setSession(credential.token, JSON.stringify(credential.user), JSON.stringify(credential.application))

    dispatch({
      type: 'LOGIN',
      payload: credential,
    })
  }

  useEffect(() => {
    const init = async () => {
      try {
        const token = localStorage.getItem('token')
        const user = localStorage.getItem('user')
        const application = localStorage.getItem('apps')

        if (token && isValidToken(token) && user && application) {
          setSession(token, user, application)
          dispatch({
            type: 'INIT',
            payload: {
              isAuthenticated: true,
              user: JSON.parse(user),
              application: JSON.parse(application),
              token
            }
          })
        } else {
          dispatch({
            type: 'INIT',
            payload: {
              isAuthenticated: false,
              user: null,
              application: [],
              token: null
            }
          })
        }
      } catch (err) {
        dispatch({
          type: 'INIT',
          payload: {
            isAuthenticated: false,
            user: null,
            application: [],
            token: null
          }
        })
      }
    }

    init()
  }, [])

  if (!state.isInitialised) {
    return <MatxLoading />
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        dispatch,
        setSession,
        glogin,
        login,
        logInFromEmail,
        logout
      }}
    >
      {children}
      <ToastContainer containerId={"LoginApp"} />
    </AuthContext.Provider>
  )
}

export default AuthContext
